<template>
    <div>
        <!-- Form Settings - hidden when fieldsOnly is true (e.g., for feedback forms) -->
        <div class="mb-6" v-if="!fieldsOnly">
            <!-- Release Date -->
            <v-datetime-picker dateFormat="dd.MM.yyyy" :label="$t('Julkaisuaika')" v-model="localForm.release_date"
                :time-picker-props="{
                    format: '24hr',
                }" :textFieldProps="{
                    outlined: 'outlined',
                    'hide-details': false,
                    class: 'mb-0',
                    rules: [v => !!v || $t('Julkaisuaika on pakollinen')]
                }" @input="emitFormUpdate">
                <template slot="dateIcon">
                    <v-icon>{{ icons.mdiCalendar }}</v-icon>
                </template>
                <template slot="timeIcon">
                    <v-icon>{{ icons.mdiClockOutline }}</v-icon>
                </template>
            </v-datetime-picker>

            <!-- Expire Date -->
            <v-datetime-picker dateFormat="dd.MM.yyyy" :label="$t('Vastausaika päättyy')"
                v-model="localForm.expire_date" :time-picker-props="{
                    format: '24hr',
                }" :textFieldProps="{
                    outlined: 'outlined',
                    'hide-details': false,
                    class: 'mb-0',
                    rules: [v => !!v || $t('Vastausaika on pakollinen')]
                }" @input="emitFormUpdate">
                <template slot="dateIcon">
                    <v-icon>{{ icons.mdiCalendar }}</v-icon>
                </template>
                <template slot="timeIcon">
                    <v-icon>{{ icons.mdiClockOutline }}</v-icon>
                </template>
            </v-datetime-picker>

            <!-- Anonymous Submissions -->
            <v-switch v-model="localForm.anonymous" :label="$t('Vastaukset kerätään anonyymisti')"
                :disabled="hasSubmissions" persistent-hint
                :hint="hasSubmissions ? $t('Tätä asetusta ei voi muuttaa sen jälkeen kun kyselyyn on vastattu') : ''"
                @change="emitFormUpdate">
            </v-switch>

            <!-- Allow Multiple Submissions -->
            <v-switch v-model="localForm.allow_multiple_submissions" class="mt-5"
                :label="$t('Salli useita vastauksia samalta käyttäjältä')" @change="emitFormUpdate">
            </v-switch>
        </div>

        <v-divider class="my-6" v-if="!fieldsOnly"></v-divider>

        <!-- Form Fields Section Header -->
        <h4 class="mb-4">{{ $t("Lomakkeen kentät") }}</h4>

        <!-- Warning when form has submissions -->
        <v-alert v-if="hasSubmissions" type="warning" dense class="mb-4">
            <template #prepend>
                <v-icon left small>{{ icons.mdiLock }}</v-icon>
            </template>
            {{ $t('Lomakkeella on vastauksia. Muokkaus rajoitettu.') }}
        </v-alert>

        <!-- Pre-warning when form has no submissions yet -->
        <v-alert v-else-if="!readonly && localFields.length > 0" type="info" dense class="mb-4">
            <template #prepend>
                <v-icon left small>{{ icons.mdiInformationOutline }}</v-icon>
            </template>
            {{ $t('Huom: Lomaketta voi muokata rajoitetusti sen jälkeen kun siihen on tullut vastauksia.') }}
        </v-alert>

        <!-- Side-by-Side Layout: Add Buttons (Left) | Field List (Right) -->
        <div class="form-builder-layout" :class="{ 'readonly-mode': readonly }">
            <!-- Left Sidebar: Add Field Buttons - hidden in readonly mode -->
            <div v-if="!readonly" class="add-field-sidebar sidebar-fields">
                <div class="">
                    <h5 class="mb-3 text-subtitle-2 grey--text text--darken-1">{{ $t("Lisää kysymys") }}</h5>
                    <v-btn color="primary" @click="addField('short-text')" block class="mb-2 justify-start" outlined>
                        <v-icon left small>{{ icons.mdiTextShort }}</v-icon>
                        {{ $t("Lyhyt vastaus") }}
                    </v-btn>
                    <v-btn color="primary" @click="addField('long-text')" block class="mb-2 justify-start" outlined>
                        <v-icon left small>{{ icons.mdiTextBoxOutline }}</v-icon>
                        {{ $t("Pitkä vastaus") }}
                    </v-btn>
                    <v-btn color="primary" @click="addField('radio')" block class="mb-2 justify-start" outlined>
                        <v-icon left small>{{ icons.mdiRadioboxMarked }}</v-icon>
                        {{ $t("Valitse yksi") }}
                    </v-btn>
                    <v-btn color="primary" @click="addField('checkbox')" block class="mb-2 justify-start" outlined>
                        <v-icon left small>{{ icons.mdiCheckboxMultipleMarked }}</v-icon>
                        {{ $t("Monivalinta") }}
                    </v-btn>
                </div>
            </div>

            <!-- Right Content: Field List -->
            <div class="field-list-container">
                <!-- Empty State -->
                <div v-if="localFields.length === 0" class="text-center py-8">
                    <p class="grey--text mt-2">
                        <v-icon size="24" color="grey lighten-2">{{ icons.mdiInformationOutline }}</v-icon>
                        {{ $t("Ei kysymyksiä. Lisää ensimmäinen kysymys vasemmalta.") }}
                    </p>
                </div>

                <!-- Fields List (Draggable) -->
                <draggable v-model="localFields" tag="div" handle=".handle" ghost-class="ghost"
                    :disabled="readonly || editingFieldIndex !== null" @end="onFieldReorder">
                    <div v-for="(field, index) in localFields" :key="field.tempId || field.id" class="mb-4">
                        <!-- Field Card - Preview View (WYSIWYG) -->
                        <v-card v-if="editingFieldIndex !== index" outlined class="field-card"
                            :class="{ 'readonly-card': readonly }" @click="!readonly && editField(index)"
                            :style="readonly ? '' : 'cursor: pointer;'">
                            <v-card-text class="py-4">
                                <!-- Field Actions Bar -->
                                <div class="d-flex align-center mb-3">
                                    <!-- Drag handle - hidden in readonly mode -->
                                    <div v-if="!readonly" class="handle mr-3"
                                        :class="{ 'drag-disabled': editingFieldIndex !== null }">
                                        <v-icon small>{{ icons.mdiCursorMove }}</v-icon>
                                    </div>
                                    <div class="d-flex align-center flex-wrap">

                                        <v-chip v-if="field.required" x-small color="error" text-color="white"
                                            class="mr-2">
                                            {{ $t('Pakollinen') }}
                                        </v-chip>
                                        <v-chip
                                            v-if="field.field_type === 1 && field.datatype && field.datatype !== 'text'"
                                            x-small outlined class="mr-2">
                                            {{ getDatatypeLabel(field.datatype) }}
                                        </v-chip>
                                    </div>
                                    <v-spacer></v-spacer>
                                    <!-- Action buttons - hidden in readonly mode -->
                                    <div v-if="!readonly" class="d-flex align-center">
                                        <v-btn x-small color="primary" @click.stop="copyField(index)" class="mr-2">
                                            <v-icon small>
                                                {{
                                                    icons.mdiContentCopy
                                                }}
                                            </v-icon>
                                        </v-btn>
                                        <v-btn x-small color="error" @click.stop="removeField(index)">
                                            <v-icon small :disabled="!canMakeBreakingChanges">
                                                {{
                                                    icons.mdiDeleteOutline
                                                }}
                                            </v-icon>
                                        </v-btn>
                                    </div>
                                </div>

                                <!-- Field Preview (WYSIWYG) -->
                                <div class="field-preview">
                                    <!-- Short Text Field Preview -->
                                    <div v-if="field.field_type === 1">
                                        <v-text-field
                                            :label="getFieldLabel(field) || $t('Klikkaa muokataksesi kenttää')"
                                            :placeholder="getFieldPlaceholder(field)" :hint="getFieldHelpText(field)"
                                            persistent-hint outlined dense readonly :type="getInputType(field.datatype)"
                                            :append-icon="field.required ? 'mdi-asterisk' : ''"
                                            :error="!getFieldLabel(field)"
                                            :error-messages="!getFieldLabel(field) ? [$t('Kentällä ei ole otsikkoa')] : []"></v-text-field>
                                    </div>

                                    <!-- Long Text Field Preview -->
                                    <div v-if="field.field_type === 2">
                                        <v-textarea :label="getFieldLabel(field) || $t('Klikkaa muokataksesi kenttää')"
                                            :placeholder="getFieldPlaceholder(field)" :hint="getFieldHelpText(field)"
                                            persistent-hint outlined dense readonly rows="3"
                                            :append-icon="field.required ? 'mdi-asterisk' : ''"
                                            :error="!getFieldLabel(field)"
                                            :error-messages="!getFieldLabel(field) ? [$t('Kentällä ei ole otsikkoa')] : []"></v-textarea>
                                    </div>

                                    <!-- Radio Field Preview -->
                                    <div v-if="field.field_type === 3">
                                        <div class="field-label mb-2">
                                            {{ getFieldLabel(field) || $t('Klikkaa muokataksesi kenttää') }}
                                            <span v-if="field.required" class="error--text">*</span>
                                        </div>
                                        <div v-if="getFieldHelpText(field)" class="text-caption grey--text mb-2">
                                            {{ getFieldHelpText(field) }}
                                        </div>
                                        <v-alert v-if="!getFieldLabel(field)" type="error" dense class="mb-2">
                                            {{ $t('Kentällä ei ole otsikkoa') }}
                                        </v-alert>
                                        <v-alert v-if="field.options.length === 0" type="warning" dense class="mb-2">
                                            {{ $t('Lisää vastausvaihtoehtoja') }}
                                        </v-alert>
                                        <v-radio-group v-if="field.options.length > 0" readonly>
                                            <v-radio v-for="option in field.options" :key="option.tempId || option.id"
                                                :label="getOptionLabel(option) || $t('Tyhjä vaihtoehto')"
                                                :value="option.tempId || option.id" readonly></v-radio>
                                        </v-radio-group>
                                    </div>

                                    <!-- Checkbox Field Preview -->
                                    <div v-if="field.field_type === 4">
                                        <div class="field-label mb-2">
                                            {{ getFieldLabel(field) || $t('Klikkaa muokataksesi kenttää') }}
                                            <span v-if="field.required" class="error--text">*</span>
                                        </div>
                                        <div v-if="getFieldHelpText(field)" class="text-caption grey--text mb-2">
                                            {{ getFieldHelpText(field) }}
                                        </div>
                                        <v-alert v-if="!getFieldLabel(field)" type="error" dense class="mb-2">
                                            {{ $t('Kentällä ei ole otsikkoa') }}
                                        </v-alert>
                                        <v-alert v-if="field.options.length === 0" type="warning" dense class="mb-2">
                                            {{ $t('Lisää vastausvaihtoehtoja') }}
                                        </v-alert>
                                        <div v-if="field.options.length > 0">
                                            <v-checkbox v-for="option in field.options"
                                                :key="option.tempId || option.id"
                                                :label="getOptionLabel(option) || $t('Tyhjä vaihtoehto')" readonly
                                                hide-details class="mt-0"></v-checkbox>
                                        </div>
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>

                        <!-- Field Card - Expanded Edit View -->
                        <v-card v-else outlined class="field-card-editing">
                            <v-card-text>
                                <!-- Field Settings Row -->
                                <div class="d-flex align-center mb-4 pb-3" style="border-bottom: 1px solid #e0e0e0;">
                                    <v-switch v-model="fieldBeingEdited.required" :label="$t('Pakollinen')"
                                        class="mr-3 mt-0"></v-switch>
                                    <v-spacer></v-spacer>
                                    <v-btn color="primary" small @click="saveField" class="mr-2">
                                        {{ $t('Tallenna') }}
                                    </v-btn>
                                    <v-btn color="grey" small @click="cancelEdit">
                                        {{ $t('Peruuta') }}
                                    </v-btn>
                                </div>

                                <!-- Data Type Selection (for short text fields only) -->
                                <div v-if="fieldBeingEdited.field_type === 1" class="mb-4">
                                    <v-select v-model="fieldBeingEdited.datatype" :items="datatypeOptions"
                                        :label="$t('Tietotyyppi')" outlined dense
                                        :disabled="!canMakeBreakingChanges && fieldBeingEdited.id"></v-select>
                                </div>

                                <!-- Field Label and Help Text Translations -->
                                <div class="mb-4">
                                    <h4 class="mb-3">{{ $t('Kentän otsikko') }}</h4>
                                    <div v-for="lang in languages" :key="lang.code" class="mb-4">
                                        <v-text-field v-model="fieldBeingEdited.translations[lang.code].label"
                                            :label="$t('Otsikko') + ' (' + lang.name + ')'" outlined dense hide-details
                                            class="mb-1">
                                            <template v-slot:prepend>
                                                <img :src="lang.icon" style="max-height: 20px" />
                                            </template>
                                        </v-text-field>
                                        <v-text-field v-model="fieldBeingEdited.translations[lang.code].help_text"
                                            :label="$t('Ohjeteksti (valinnainen)')" outlined dense hide-details
                                            class="ml-10 mt-4">
                                        </v-text-field>
                                    </div>
                                </div>

                                <!-- Options Section (for radio/checkbox fields) -->
                                <div v-if="fieldBeingEdited.field_type === 3 || fieldBeingEdited.field_type === 4"
                                    class="mb-4">
                                    <div class="d-flex align-center mb-3">
                                        <h4>{{ $t('Vastausvaihtoehdot') }}</h4>
                                        <v-spacer></v-spacer>
                                    </div>

                                    <draggable v-model="fieldBeingEdited.options" tag="div" handle=".option-handle"
                                        ghost-class="ghost" @end="onOptionReorder">
                                        <div v-for="(option, optionIndex) in fieldBeingEdited.options"
                                            :key="option.tempId || option.id" class="option-section mb-4">
                                            <div class="d-flex align-center mb-2">
                                                <div class="option-handle mr-2">
                                                    <v-icon small>{{ icons.mdiCursorMove }}</v-icon>
                                                </div>
                                                <span class="font-weight-medium">{{ $t('Vaihtoehto') }} {{ optionIndex +
                                                    1
                                                    }}</span>
                                                <v-spacer></v-spacer>
                                                <v-btn x-small color="error" @click="removeFieldOption(optionIndex)"
                                                    :disabled="!canMakeBreakingChanges">
                                                    <v-icon color="white" small>{{ icons.mdiDeleteOutline }}</v-icon>
                                                </v-btn>
                                            </div>

                                            <v-text-field v-for="lang in languages"
                                                :key="'option-' + option.tempId + '-' + lang.code"
                                                v-model="option.translations[lang.code].label"
                                                :label="$t('Vastausvaihtoehto') + ' (' + lang.name + ')'" outlined dense
                                                class="mb-1">
                                                <template v-slot:prepend>
                                                    <img :src="lang.icon" style="max-height: 20px" />
                                                </template>
                                            </v-text-field>
                                        </div>
                                    </draggable>

                                    <v-btn color="primary" x-small @click="addFieldOption" class="ml-2">
                                        {{ $t('Lisää vaihtoehto') }}
                                    </v-btn>

                                    <v-alert v-if="fieldBeingEdited.options.length === 0" type="warning" dense
                                        class="mt-2">
                                        {{ $t('Lisää vähintään yksi vastausvaihtoehto') }}
                                    </v-alert>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>
                </draggable>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch, computed, reactive } from '@vue/composition-api';
import draggable from 'vuedraggable';
import {
    mdiTextShort,
    mdiTextBoxOutline,
    mdiRadioboxMarked,
    mdiCheckboxMultipleMarked,
    mdiInformationOutline,
    mdiCursorMove,
    mdiAlertCircle,
    mdiContentCopy,
    mdiDeleteOutline,
    mdiPlus,
    mdiCalendar,
    mdiClockOutline,
    mdiLock,
} from '@mdi/js';
import { i18n } from '@/plugins/i18n';
import { validateFormFields } from '@/@core/utils/form_helpers';

export default {
    name: 'FormEditor',
    components: {
        draggable,
    },
    props: {
        formData: {
            type: Object,
            required: true,
        },
        languages: {
            type: Array,
            required: true,
        },
        hasSubmissions: {
            type: Boolean,
            default: false,
        },
        // When true, only shows the field editor (hides form settings like dates, anonymous, etc.)
        // Used for feedback forms where form settings are ignored
        fieldsOnly: {
            type: Boolean,
            default: false,
        },
        // When true, form is displayed in read-only mode (no editing, adding, or reordering)
        // Used when category is active and form cannot be edited
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { emit }) {
        const localFields = ref([]);
        const editingFieldIndex = ref(null);
        const fieldBeingEdited = ref(null);
        const localForm = reactive({
            id: null,
            release_date: '',
            expire_date: '',
            anonymous: true,
            allow_multiple_submissions: false,
        });

        // Initialize form data from props
        watch(
            () => props.formData,
            (newFormData) => {
                if (newFormData) {
                    localForm.id = newFormData.id;
                    localForm.release_date = newFormData.release_date || '';
                    localForm.expire_date = newFormData.expire_date || '';
                    localForm.anonymous = newFormData.anonymous !== undefined ? newFormData.anonymous : true;
                    localForm.allow_multiple_submissions = newFormData.allow_multiple_submissions || false;

                    if (newFormData.fields && newFormData.fields.length > 0) {
                        localFields.value = JSON.parse(JSON.stringify(newFormData.fields));
                    }
                }
            },
            { immediate: true, deep: true }
        );

        // Emit editing state changes to parent
        watch(editingFieldIndex, (newVal) => {
            emit('editing-state-changed', newVal !== null);
        });

        // Emit complete form data to parent
        const emitFormUpdate = () => {
            // When fieldsOnly mode (feedback forms), set date fields to null
            // and use fixed defaults for anonymous and allow_multiple_submissions
            const formData = {
                id: localForm.id,
                release_date: props.fieldsOnly ? null : localForm.release_date,
                expire_date: props.fieldsOnly ? null : localForm.expire_date,
                anonymous: props.fieldsOnly ? false : localForm.anonymous,
                allow_multiple_submissions: props.fieldsOnly ? true : localForm.allow_multiple_submissions,
                fields: JSON.parse(JSON.stringify(localFields.value)),
            };
            emit('update:form', formData);
        };

        // Emit changes when fields change
        const emitFieldsUpdate = () => {
            emitFormUpdate();
        };

        // Field type options
        const fieldTypeMap = {
            'short-text': 1,
            'long-text': 2,
            'radio': 3,
            'checkbox': 4,
        };

        // Datatype options for short text fields
        const datatypeOptions = computed(() => [
            { text: i18n.t('Teksti'), value: 'text' },
            { text: i18n.t('Päivämäärä'), value: 'date' },
        ]);

        // Check if breaking changes are allowed (no submissions)
        const canMakeBreakingChanges = computed(() => {
            return !props.hasSubmissions;
        });

        // Helper functions
        const getFieldTypeIcon = (type) => {
            switch (type) {
                case 1: return mdiTextShort;
                case 2: return mdiTextBoxOutline;
                case 3: return mdiRadioboxMarked;
                case 4: return mdiCheckboxMultipleMarked;
                default: return mdiInformationOutline;
            }
        };

        const getFieldTypeColor = (type) => {
            switch (type) {
                case 1: return 'blue';
                case 2: return 'green';
                case 3: return 'orange';
                case 4: return 'purple';
                default: return 'grey';
            }
        };

        const getFieldTypeLabel = (type) => {
            switch (type) {
                case 1: return i18n.t('Tekstikenttä');
                case 2: return i18n.t('Monirivinen tekstikenttä');
                case 3: return i18n.t('Valinta');
                case 4: return i18n.t('Monivalinta');
                default: return i18n.t('Tuntematon');
            }
        };

        const getDatatypeLabel = (datatype) => {
            const option = datatypeOptions.value.find(opt => opt.value === datatype);
            return option ? option.text : datatype;
        };

        const getFieldLabel = (field) => {
            // Get field label from the first available language
            for (const lang of props.languages) {
                if (field.translations[lang.code] && field.translations[lang.code].label) {
                    return field.translations[lang.code].label;
                }
            }
            return '';
        };

        const getFieldHelpText = (field) => {
            // Get field help text from the first available language
            for (const lang of props.languages) {
                if (field.translations[lang.code] && field.translations[lang.code].help_text) {
                    return field.translations[lang.code].help_text;
                }
            }
            return '';
        };

        const getFieldPlaceholder = (field) => {
            // Generate a placeholder based on field type and datatype
            if (field.field_type === 1) {
                switch (field.datatype) {
                    case 'email':
                        return 'esimerkki@email.com';
                    case 'number':
                        return '123';
                    case 'date':
                        return 'pp.kk.vvvv';
                    case 'time':
                        return 'hh:mm';
                    default:
                        return '';
                }
            }
            return '';
        };

        const getInputType = (datatype) => {
            // Map datatype to HTML input type
            switch (datatype) {
                case 'email':
                    return 'email';
                case 'number':
                    return 'number';
                case 'date':
                    return 'date';
                case 'time':
                    return 'time';
                default:
                    return 'text';
            }
        };

        const getOptionLabel = (option) => {
            // Get option label from the first available language
            for (const lang of props.languages) {
                if (option.translations[lang.code] && option.translations[lang.code].label) {
                    return option.translations[lang.code].label;
                }
            }
            return '';
        };

        // Field operations
        const addField = (type) => {
            const fieldType = fieldTypeMap[type];
            const newField = {
                tempId: Date.now() + Math.random(),
                id: null,
                field_type: fieldType,
                datatype: fieldType === 1 ? 'text' : null,
                required: false,
                translations: {},
                options: [],
            };

            // Initialize translations for all languages
            props.languages.forEach((lang) => {
                newField.translations[lang.code] = {
                    label: '',
                    help_text: '',
                };
            });

            // Add default options for radio/checkbox
            if (fieldType === 3 || fieldType === 4) {
                addDefaultOptions(newField);
            }

            localFields.value.push(newField);
            emitFieldsUpdate();

            // Auto-open for editing
            editField(localFields.value.length - 1);
        };

        const addDefaultOptions = (field) => {
            for (let i = 0; i < 2; i++) {
                const option = {
                    tempId: Date.now() + Math.random() + i,
                    id: null,
                    translations: {},
                };
                props.languages.forEach((lang) => {
                    option.translations[lang.code] = {
                        label: '',
                    };
                });
                field.options.push(option);
            }
        };

        const editField = (index) => {
            // Save any currently editing field first
            if (editingFieldIndex.value !== null && editingFieldIndex.value !== index) {
                saveField();
            }

            editingFieldIndex.value = index;
            fieldBeingEdited.value = JSON.parse(JSON.stringify(localFields.value[index]));

            // Ensure all language translations exist
            props.languages.forEach((lang) => {
                if (!fieldBeingEdited.value.translations[lang.code]) {
                    fieldBeingEdited.value.translations[lang.code] = {
                        label: '',
                        help_text: '',
                    };
                }
            });

            // Ensure options have all language translations
            if (fieldBeingEdited.value.options) {
                fieldBeingEdited.value.options.forEach((option) => {
                    props.languages.forEach((lang) => {
                        if (!option.translations[lang.code]) {
                            option.translations[lang.code] = {
                                label: '',
                            };
                        }
                    });
                });
            }
        };

        const saveField = () => {
            if (editingFieldIndex.value !== null && fieldBeingEdited.value) {
                localFields.value[editingFieldIndex.value] = JSON.parse(JSON.stringify(fieldBeingEdited.value));
                editingFieldIndex.value = null;
                fieldBeingEdited.value = null;
                emitFieldsUpdate();
            }
        };

        const cancelEdit = () => {
            editingFieldIndex.value = null;
            fieldBeingEdited.value = null;
        };

        const removeField = (index) => {
            if (confirm(i18n.t('Haluatko varmasti poistaa kentän?'))) {
                localFields.value.splice(index, 1);
                if (editingFieldIndex.value === index) {
                    editingFieldIndex.value = null;
                    fieldBeingEdited.value = null;
                } else if (editingFieldIndex.value > index) {
                    editingFieldIndex.value--;
                }
                emitFieldsUpdate();
            }
        };

        const copyField = (index) => {
            const originalField = localFields.value[index];
            const copiedField = {
                tempId: Date.now() + Math.random(),
                id: null,
                field_type: originalField.field_type,
                datatype: originalField.datatype,
                required: originalField.required,
                translations: {},
                options: [],
            };

            // Copy translations
            props.languages.forEach((lang) => {
                copiedField.translations[lang.code] = {
                    label: originalField.translations[lang.code]?.label || '',
                    help_text: originalField.translations[lang.code]?.help_text || '',
                };
            });

            // Copy options if applicable
            if (originalField.field_type === 3 || originalField.field_type === 4) {
                originalField.options.forEach((option) => {
                    const copiedOption = {
                        tempId: Date.now() + Math.random(),
                        id: null,
                        translations: {},
                    };
                    props.languages.forEach((lang) => {
                        copiedOption.translations[lang.code] = {
                            label: option.translations[lang.code]?.label || '',
                        };
                    });
                    copiedField.options.push(copiedOption);
                });
            }

            localFields.value.push(copiedField);
            emitFieldsUpdate();
        };

        const addFieldOption = () => {
            if (fieldBeingEdited.value) {
                const newOption = {
                    tempId: Date.now() + Math.random(),
                    id: null,
                    translations: {},
                };
                props.languages.forEach((lang) => {
                    newOption.translations[lang.code] = {
                        label: '',
                    };
                });
                fieldBeingEdited.value.options.push(newOption);
            }
        };

        const removeFieldOption = (index) => {
            if (confirm(i18n.t('Haluatko varmasti poistaa vastausvaihtoehdon?'))) {
                fieldBeingEdited.value.options.splice(index, 1);
            }
        };

        const onFieldReorder = () => {
            emitFieldsUpdate();
        };

        const onOptionReorder = () => {
            // Force Vue to recognize the options array has changed
            // by creating a new reference
            if (fieldBeingEdited.value && fieldBeingEdited.value.options) {
                fieldBeingEdited.value.options = [...fieldBeingEdited.value.options];
            }
        };

        /**
         * Validates form fields and returns an array of validation errors.
         * Can be called by parent components via ref.
         * @returns {Array} Array of error objects { fieldIndex, identifier, message }
         */
        const validateFields = () => {
            return validateFormFields(localFields.value, props.languages, i18n);
        };

        return {
            localForm,
            localFields,
            editingFieldIndex,
            fieldBeingEdited,
            datatypeOptions,
            canMakeBreakingChanges,
            getFieldTypeIcon,
            getFieldTypeColor,
            getFieldTypeLabel,
            getDatatypeLabel,
            getFieldLabel,
            getFieldHelpText,
            getFieldPlaceholder,
            getInputType,
            getOptionLabel,
            addField,
            editField,
            saveField,
            cancelEdit,
            removeField,
            copyField,
            addFieldOption,
            removeFieldOption,
            onFieldReorder,
            onOptionReorder,
            emitFormUpdate,
            validateFields,
            icons: {
                mdiTextShort,
                mdiTextBoxOutline,
                mdiRadioboxMarked,
                mdiCheckboxMultipleMarked,
                mdiInformationOutline,
                mdiCursorMove,
                mdiAlertCircle,
                mdiContentCopy,
                mdiDeleteOutline,
                mdiPlus,
                mdiCalendar,
                mdiClockOutline,
                mdiLock,
            },
        };
    },
};
</script>

<style scoped>
/* Side-by-side layout */
.form-builder-layout {
    display: flex;
    gap: 24px;
    align-items: flex-start;
}

.add-field-sidebar {
    flex-shrink: 0;
    width: 300px;
    position: sticky;
    top: 100px;
}

.sidebar-fields {
    background: #f5f5f5;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e0e0e0;
}

.field-list-container {
    flex: 1;
    min-width: 0;
}

/* Responsive: Stack vertically on smaller screens */
@media (max-width: 960px) {
    .form-builder-layout {
        flex-direction: column;
    }

    .add-field-sidebar {
        width: 100%;
    }

    .sidebar-sticky {
        position: relative;
        top: 0;
        width: 100%;
    }

    .field-list-container {
        margin-left: 0;
    }
}

.field-card {
    transition: all 0.2s ease;
    background-color: #fafafa;
}

.field-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: #ffffff;
}

/* Readonly mode styles */
.readonly-mode {
    /* In readonly mode, the layout should just show the field list */
}

.readonly-card {
    cursor: default !important;
}

.readonly-card:hover {
    box-shadow: none;
    background-color: #fafafa;
}

.field-card-editing {
    border: 2px solid #1976d2 !important;
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
}

.field-preview {
    background-color: #ffffff;
    border-radius: 4px;
    padding: 12px;
}

.field-label {
    font-weight: 500;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.87);
}

.option-section {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 16px;
    background-color: #f9f9f9;
}

.handle,
.option-handle {
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
}

.handle:hover,
.option-handle:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

.handle:active,
.option-handle:active {
    cursor: grabbing;
}

.drag-disabled {
    cursor: not-allowed !important;
    opacity: 0.5;
}

.drag-disabled:hover {
    background-color: transparent !important;
}

.ghost {
    opacity: 0.5;
    background-color: #e3f2fd;
    border: 2px dashed #1976d2;
}

/* Make the preview fields feel more realistic but clearly indicate they're not interactive */
.field-preview>>>.v-input--is-readonly {
    pointer-events: none;
}

.field-preview>>>.v-text-field__details,
.field-preview>>>.v-messages {
    margin-top: 4px;
}
</style>
